import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  message,
  notification,
} from 'tea-component';
import CoverUploader from '@gp/components/CoverUploader';
import {
  ISearchRef,
  UseFormSearchGP,
} from './Components/UseFormSearchGP/index';
import './index.scss';
import { useEditProjectDetailInfo } from '@gp/pages/Pipeline/ProjectDetails/GameDetail/components/TeamGameInformation/hooks/useInformation';
import { useMemoizedFn, useSafeState, useDeepCompareEffect } from 'ahooks';
import { find, get, isArray, isEmpty } from 'lodash-es';
import {
  formatFileSize,
  parseFileSize,
  formatDateTimeStrByTimestamp,
  reportBtnOperationLog,
} from '@gp/utils/common';
import { CONSTANT_TEXTS, FIELD_COMMON_ERR_MSG } from '@gp/constants/const';
import DEFAULT_GAME_ICON from '@/pages/dashboard/assets/svg/mobile-game-default.svg';
import type { ICover } from '@gp/components/CoverUploader/types';
type GameOverviewEdit = {
  visible: boolean;
  clone: (...args: any[]) => void;
  [key: string]: any;
};

const STAGE_OPTIONS: IGPSelectOptionWithGroup[] = [
  {
    value: 'Live',
    text: 'Live',
  },
  {
    value: 'Development',
    text: 'Development',
  },
  {
    value: 'Legacy',
    text: 'Legacy',
  },
  {
    value: 'Pre-planned',
    text: 'Pre-planned',
  },
  {
    value: 'Planned',
    text: 'Planned',
  },
  {
    value: 'Canceled',
    text: CONSTANT_TEXTS.CANCELED,
  },
  {
    value: 'No Maintenance',
    text: 'No Maintenance',
  },
];

const convertStageValue = (value?: string): string => {
  if (!value) return '';
  const stageOption = STAGE_OPTIONS.find(
    (item) => item.value.toLowerCase() === value.toLowerCase(),
  );
  return stageOption?.value || value;
};

const GameOverviewEdit = ({
  visible,
  close,
  ...restProps
}: GameOverviewEdit) => {
  const {
    detailInfo,
    gameInformationDataRefresh,
    milestoneData,
    milestoneDataRefresh,
    projectsRecordsRefresh,
    projectsRefresh,
  } = restProps || {};
  const { studio_entity_id, project_id } = detailInfo || {};
  const { runAsync } = useEditProjectDetailInfo('infoEdit');
  const [cover, setCover] = useSafeState({});
  const searchFormRef = useRef<ISearchRef>();
  const nextMilestonesList = useMemo(() => {
    return milestoneData?.list?.map((task: { milestone: any; }) => ({
      value: task.milestone,
      text: task.milestone,
    }));
  }, [milestoneData?.list]);
  useDeepCompareEffect(() => {
    searchFormRef.current?.form?.change(
      'current_milestone',
      findPreviousItemFn(
        nextMilestonesList,
        searchFormRef.current?.restFormProps?.values?.next_milestone,
      ) || nextMilestonesList?.[0]?.value,
    );
  }, [searchFormRef.current?.restFormProps]);

  const config = [
    {
      name: 'project',
      label: 'Project name',
      component: (
        <Input
          placeholder="Please input Project name"
          className="dbg-game-overview-form-input"
        />
      ),
      rules: { message: FIELD_COMMON_ERR_MSG, required: true },
    },
    {
      name: 'stage',
      label: 'Stage',
      component: (
        <Select
          size="m"
          className="dbg-game-overview-form-input"
          appearance="button"
          options={STAGE_OPTIONS}
          matchButtonWidth
          placeholder="Please input Stage"
        />
      ),
      rules: { message: FIELD_COMMON_ERR_MSG, required: true },
    },
  ];
  const getFormValue = async <
    V extends {
      cover: string;
      [key: string]: unknown;
    },
  >(
    value: V,
  ) => {
    try {
      const params: V = {
        studio_entity_id,
        project_id,
        next_milestone_date: get(
          find(milestoneData?.list, { milestone: value?.next_milestone }),
          'date',
          '',
        ),
        ...value,
        current_milestone: isEmpty(value?.current_milestone)
          ? ''
          : value?.current_milestone,
      };
      // 太随意了...如果返回的为null，必定是删除了，需要给后端应该空''
      if (cover) {
        (params.cover = cover?.cover
          ? cover?.cover
          : cover?.cover === null
          ? ''
          : detailInfo?.cover),
          (params.image_name = cover?.image_name
            ? cover?.image_name
            : cover?.image_name === null
            ? ''
            : detailInfo?.image_name),
          (params.image_size = cover?.image_size
            ? cover?.image_size
            : cover?.image_size === null
            ? ''
            : detailInfo?.image_size);
        params.upload_day = cover?.upload_day
          ? cover?.upload_day
          : cover?.upload_day === ''
          ? ''
          : detailInfo?.upload_day;
      }
      await runAsync(params);
      gameInformationDataRefresh?.();
      milestoneDataRefresh?.();
      projectsRecordsRefresh?.();
      projectsRefresh?.();
      notification.success({
        description: 'Save success',
        duration: 1000,
      });
      close();
    } catch (error) {
      // notification.error({
      //   description: 'Save failed. Please check the network',
      //   duration: 1000,
      // });
    }
  };

  const handleUserSubmit = () => {
    searchFormRef.current?.handleSubmit?.();
    reportBtnOperationLog('DETAIL_PROJECT_DETAIL_OVERVIEW_EDIT_SUBMIT');
  };
  const SearchComponent = UseFormSearchGP({
    config,
    getFormValue: (value) => getFormValue(value),
    labelSpan: 6,
    reset: visible,
    isClose: false,
    initialValues: {
      project: detailInfo?.project,
      stage: convertStageValue(detailInfo?.stage),
      current_milestone: detailInfo?.current_milestone,
      next_milestone: detailInfo?.next_milestone,
    },
    layout: 'inline',
    isShowSearchBtn: false,
    isDisabledSearchBtn: true,
    className: 'dbg-game-overview-form',
    ref: searchFormRef,
  });
  const getUploadSuccessMessage = useMemoizedFn(
    (uploadResMessage, file, uploadDate, fileSize) => {
      setCover({
        cover: uploadResMessage?.url || null,
        image_size: fileSize || null,
        upload_day: uploadDate,
        image_name: file?.name || null,
      });
    },
  );

  return (
    <Modal
      size={800}
      className="dbg-game-overview-edit"
      visible={visible}
      caption="Game Overview"
      onClose={() => {
        close();
      }}
    >
      <Modal.Body>
        <CatchComponents
          {...{
            detailInfo: useMemo(() => detailInfo, [detailInfo]),
            getUploadSuccessMessage,
          }}
        ></CatchComponents>
        {SearchComponent}
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" onClick={handleUserSubmit}>
          Confirm
        </Button>
        <Button
          type="weak"
          onClick={() => {
            close();
          }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GameOverviewEdit;

const CatchComponents = memo((props: any) => {
  const { detailInfo, getUploadSuccessMessage } = props;
  const {
    project_id: projectId,
    cover,
    image_name: coverName,
    image_size: coverSize,
    upload_day: coverUploadTime,
  } = detailInfo || {};
  const coverValue = useMemo<ICover>(
    () => ({
      cover,
      name: coverName,
      file_size: parseFileSize(coverSize),
      timestamp: coverUploadTime,
    }),
    [cover, coverName, coverSize, coverUploadTime],
  );
  return (
    <CoverUploader
      style={{ marginBottom: 20 }}
      uniqueKey={projectId}
      errorImg={DEFAULT_GAME_ICON}
      value={coverValue}
      onUploadSuccess={(payload: ICover) => {
        const {
          name: coverName,
          cover: coverUrl,
          file_size: fileSize,
          timestamp,
        } = payload || {};
        const coverUploadTime = formatDateTimeStrByTimestamp(timestamp);
        const fileSizeFormatted = formatFileSize(fileSize);
        getUploadSuccessMessage(
          { url: coverUrl },
          { name: coverName },
          coverUploadTime,
          fileSizeFormatted,
        );
      }}
    />
  );
});

const findPreviousItemFn = (milestones: any[], key: string) => {
  if (isEmpty(milestones) || !isArray(milestones)) return [];
  const currentList = milestones?.findIndex(
    (task: { value: string; }) => task?.value === key,
  );
  return milestones[currentList - 1]?.value;
};
